import {
  activateControl,
  consumeKeyboardEvent,
  focusControl,
  getCrudSearchControlLabel,
  getCrudSearchControls,
  getTopActiveDialog,
  isCrudSearchShortcut,
  isEditableKeyboardTarget,
  isElementVisible,
  trapTabWithin,
} from "@/utils/crudKeyboard";

export const STANDALONE_CRUD_SHORTCUTS_EVENT =
  "standalone-crud-keyboard-shortcuts:open";
export const STANDALONE_CRUD_SEARCH_EVENT =
  "standalone-crud-keyboard-search:open";

const ROOT_SELECTOR = ".crud-standalone-listagem";
const TABLE_SELECTOR = ".v-data-table";
const ACTIVE_TABLE_CLASS = "crud-standalone-keyboard-table";
const ACTIVE_ROW_CLASS = "crud-listagem-table__row--keyboard-active";
const EDIT_HANDLER_NAMES = [
  "openEditarModal",
  "confirmaUpdate",
  "openEdit",
  "editarRegistroTarefa",
];
const INSERT_HANDLER_NAMES = [
  "openInsertModal",
  "confirmaInsert",
  "openCreate",
];
const componentStates = new WeakMap();

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function getState(component) {
  if (!componentStates.has(component)) {
    componentStates.set(component, {
      activeRowIndex: -1,
      activeTable: null,
      frameId: null,
      observer: null,
      initialized: false,
    });
  }
  return componentStates.get(component);
}

function visibleElements(container, selector) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(selector)).filter(
    isElementVisible
  );
}

function tableRows(table) {
  if (!table) return [];
  return visibleElements(table, "tbody tr").filter(
    (row) =>
      !row.classList.contains("v-data-table__empty-wrapper") &&
      !row.classList.contains("v-data-table__expanded__content") &&
      row.querySelector("td")
  );
}

function controlText(control) {
  return normalizeText(
    [
      control?.getAttribute?.("aria-label"),
      control?.getAttribute?.("title"),
      control?.textContent,
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function isUsableControl(control) {
  return Boolean(
    control &&
      isElementVisible(control) &&
      !control.disabled &&
      control.getAttribute("aria-disabled") !== "true" &&
      !control.classList.contains("v-btn--disabled") &&
      !control.classList.contains("v-btn--loading")
  );
}

export default {
  mounted() {
    this.$nextTick(this.initializeStandaloneCrudKeyboard);
  },
  activated() {
    this.$nextTick(this.scheduleStandaloneCrudKeyboardSync);
  },
  beforeDestroy() {
    this.destroyStandaloneCrudKeyboard();
  },
  methods: {
    initializeStandaloneCrudKeyboard() {
      if (!this.$el?.matches?.(ROOT_SELECTOR)) return;
      const state = getState(this);
      if (state.initialized) return;

      state.initialized = true;
      window.addEventListener(
        "keydown",
        this.handleStandaloneCrudKeyboardKeydown
      );
      window.addEventListener(
        "focusin",
        this.handleStandaloneCrudKeyboardFocus
      );
      window.addEventListener(
        "click",
        this.handleStandaloneCrudKeyboardClick,
        true
      );

      if (typeof MutationObserver !== "undefined") {
        state.observer = new MutationObserver(
          this.scheduleStandaloneCrudKeyboardSync
        );
        state.observer.observe(this.$el, { childList: true, subtree: true });
      }
      this.syncStandaloneCrudKeyboardTables();
    },
    destroyStandaloneCrudKeyboard() {
      const state = componentStates.get(this);
      if (!state?.initialized) return;

      window.removeEventListener(
        "keydown",
        this.handleStandaloneCrudKeyboardKeydown
      );
      window.removeEventListener(
        "focusin",
        this.handleStandaloneCrudKeyboardFocus
      );
      window.removeEventListener(
        "click",
        this.handleStandaloneCrudKeyboardClick,
        true
      );
      state.observer?.disconnect?.();
      if (
        state.frameId !== null &&
        typeof cancelAnimationFrame === "function"
      ) {
        cancelAnimationFrame(state.frameId);
      }
      componentStates.delete(this);
    },
    scheduleStandaloneCrudKeyboardSync() {
      const state = componentStates.get(this);
      if (!state?.initialized || state.frameId !== null) return;

      const schedule =
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame
          : (callback) => setTimeout(callback, 0);
      state.frameId = schedule(() => {
        state.frameId = null;
        this.syncStandaloneCrudKeyboardTables();
      });
    },
    isStandaloneCrudKeyboardPageActive() {
      if (!isElementVisible(this.$el)) return false;
      const pages = visibleElements(document, ROOT_SELECTOR);
      return pages[pages.length - 1] === this.$el;
    },
    standaloneCrudKeyboardContainer() {
      const dialog = getTopActiveDialog(this.$el?.ownerDocument);
      return dialog || this.$el;
    },
    standaloneCrudKeyboardHasBaseFormDialog() {
      const dialog = getTopActiveDialog(this.$el?.ownerDocument);
      return Boolean(dialog?.querySelector?.(".crud-form-page"));
    },
    standaloneCrudKeyboardTables(container) {
      return visibleElements(container, TABLE_SELECTOR).filter(
        (table) => tableRows(table).length || !table.querySelector("tbody")
      );
    },
    syncStandaloneCrudKeyboardTables() {
      if (!this.isStandaloneCrudKeyboardPageActive()) return;
      const container = this.standaloneCrudKeyboardContainer();
      this.standaloneCrudKeyboardTables(container).forEach((table) => {
        table.classList.add(ACTIVE_TABLE_CLASS);
        table.setAttribute("tabindex", "0");
        table.setAttribute("aria-keyshortcuts", "ArrowUp ArrowDown Enter");
      });

      const state = getState(this);
      if (!isElementVisible(state.activeTable)) {
        state.activeTable = null;
        state.activeRowIndex = -1;
      }
      if (state.activeTable) this.syncStandaloneCrudKeyboardActiveRow();
    },
    standaloneCrudKeyboardResolveTable(target) {
      const container = this.standaloneCrudKeyboardContainer();
      const targetTable = target?.closest?.(`.${ACTIVE_TABLE_CLASS}`);
      if (targetTable && container.contains(targetTable)) return targetTable;

      const state = getState(this);
      if (
        state.activeTable &&
        container.contains(state.activeTable) &&
        isElementVisible(state.activeTable)
      ) {
        return state.activeTable;
      }

      const tables = this.standaloneCrudKeyboardTables(container);
      return (
        tables.sort((left, right) => {
          const classDifference =
            Number(right.classList.contains("crud-listagem-table")) -
            Number(left.classList.contains("crud-listagem-table"));
          if (classDifference) return classDifference;
          return tableRows(right).length - tableRows(left).length;
        })[0] || null
      );
    },
    setStandaloneCrudKeyboardActiveRow(table, index, focus = false) {
      const rows = tableRows(table);
      if (!rows.length) return false;
      const state = getState(this);
      state.activeTable = table;
      state.activeRowIndex = Math.max(0, Math.min(rows.length - 1, index));
      this.syncStandaloneCrudKeyboardActiveRow();
      if (focus) rows[state.activeRowIndex]?.focus?.();
      return true;
    },
    syncStandaloneCrudKeyboardActiveRow() {
      const state = getState(this);
      const tables = visibleElements(
        this.standaloneCrudKeyboardContainer(),
        `.${ACTIVE_TABLE_CLASS}`
      );
      tables.forEach((table) => {
        tableRows(table).forEach((row) => {
          row.classList.remove(ACTIVE_ROW_CLASS);
          row.setAttribute("tabindex", "-1");
        });
      });

      const rows = tableRows(state.activeTable);
      if (!rows.length) {
        state.activeRowIndex = -1;
        return;
      }
      state.activeRowIndex = Math.max(
        0,
        Math.min(rows.length - 1, state.activeRowIndex)
      );
      const row = rows[state.activeRowIndex];
      row.classList.add(ACTIVE_ROW_CLASS);
      row.setAttribute("tabindex", "0");
    },
    ensureStandaloneCrudKeyboardActiveRow(table) {
      const state = getState(this);
      const rows = tableRows(table);
      if (!rows.length) return false;
      const index = state.activeTable === table ? state.activeRowIndex : 0;
      return this.setStandaloneCrudKeyboardActiveRow(table, index, false);
    },
    moveStandaloneCrudKeyboardActiveRow(table, direction) {
      const state = getState(this);
      const rows = tableRows(table);
      if (!rows.length) return false;
      const currentIndex =
        state.activeTable === table ? state.activeRowIndex : -1;
      const nextIndex =
        currentIndex < 0
          ? direction > 0
            ? 0
            : rows.length - 1
          : currentIndex + direction;
      return this.setStandaloneCrudKeyboardActiveRow(table, nextIndex, true);
    },
    standaloneCrudKeyboardActiveRow(table) {
      const state = getState(this);
      if (state.activeTable !== table) return null;
      return tableRows(table)[state.activeRowIndex] || null;
    },
    standaloneCrudKeyboardHasHandler(names) {
      return names.some((name) => typeof this[name] === "function");
    },
    standaloneCrudKeyboardEditControl(row) {
      if (!row || !this.standaloneCrudKeyboardHasHandler(EDIT_HANDLER_NAMES)) {
        return null;
      }

      const namedControl = visibleElements(
        row,
        "[data-crud-edit], [name='editBtn'], #editBtn"
      ).find(isUsableControl);
      if (namedControl) return namedControl;

      const editLabels = [this.$t?.("Editar"), "Editar", "Edit"].map(
        normalizeText
      );
      return visibleElements(row, "button, [role='button'], .v-icon").find(
        (control) => {
          if (!isUsableControl(control)) return false;
          const label = controlText(control);
          const icon = control.querySelector?.(
            ".mdi-pencil, .mdi-pencil-outline, .ni-ruler-pencil"
          );
          return (
            icon || editLabels.some((item) => item && label.includes(item))
          );
        }
      );
    },
    activateStandaloneCrudKeyboardRow(table) {
      const row = this.standaloneCrudKeyboardActiveRow(table);
      return activateControl(this.standaloneCrudKeyboardEditControl(row));
    },
    standaloneCrudKeyboardInsertControl(container) {
      if (!this.standaloneCrudKeyboardHasHandler(INSERT_HANDLER_NAMES)) {
        return null;
      }
      const explicit = visibleElements(container, "#btn--cadastro").find(
        isUsableControl
      );
      if (explicit) return explicit;

      return visibleElements(container, "button").find(
        (button) =>
          isUsableControl(button) &&
          button.querySelector(".mdi-plus, .mdi-plus-circle")
      );
    },
    standaloneCrudKeyboardFilterControl(container) {
      return visibleElements(container, "#btn--filtrar").find(isUsableControl);
    },
    standaloneCrudKeyboardSearchControls(container) {
      return getCrudSearchControls(container);
    },
    openStandaloneCrudKeyboardSearch(container) {
      const controls = this.standaloneCrudKeyboardSearchControls(container);
      if (!controls.length) return false;
      if (controls.length === 1) return focusControl(controls[0]);

      const targets = {};
      const fields = controls.map((control, index) => {
        const id = `standalone-search-field-${index}`;
        targets[id] = control;
        return {
          id,
          label: getCrudSearchControlLabel(
            control,
            `${this.$t?.("Campo_de_pesquisa") || "Campo de pesquisa"} ${
              index + 1
            }`
          ),
        };
      });
      this.$root.$emit(STANDALONE_CRUD_SEARCH_EVENT, {
        fields,
        onSelect: (fieldId) => {
          this.$nextTick(() => focusControl(targets[fieldId]));
        },
      });
      return true;
    },
    standaloneCrudKeyboardSelectionControl(row) {
      return visibleElements(
        row,
        "[role='checkbox'], input[type='checkbox'], .v-simple-checkbox"
      ).find(isUsableControl);
    },
    standaloneCrudKeyboardSelectAllControl(table) {
      return visibleElements(
        table,
        "thead [role='checkbox'], thead input[type='checkbox'], thead .v-simple-checkbox"
      ).find(isUsableControl);
    },
    standaloneCrudKeyboardSafeCloseControl(dialog) {
      const safeLabels = [
        this.$t?.("Cancelar"),
        this.$t?.("Fechar"),
        this.$t?.("nao"),
        this.$t?.("Não"),
        this.$t?.("Voltar"),
        "Cancelar",
        "Fechar",
        "Não",
        "No",
        "Cancel",
        "Close",
        "Volver",
      ]
        .map(normalizeText)
        .filter(Boolean);
      return visibleElements(dialog, "button, [role='button'], .v-icon").find(
        (control) => {
          if (!isUsableControl(control)) return false;
          if (
            control.querySelector?.(".mdi-close") ||
            control.matches?.(".mdi-close")
          ) {
            return true;
          }
          const label = controlText(control);
          return safeLabels.some(
            (safeLabel) =>
              label === safeLabel || label.startsWith(`${safeLabel} `)
          );
        }
      );
    },
    standaloneCrudKeyboardShortcuts(container, table) {
      const shortcuts = [];
      if (this.standaloneCrudKeyboardInsertControl(container)) {
        shortcuts.push({
          teclas: "Ctrl / ⌘ + I",
          descricao: "Incluir_um_novo_registro",
        });
      }
      if (this.standaloneCrudKeyboardSearchControls(container).length) {
        shortcuts.push({
          teclas: "Ctrl / ⌘ + Alt / ⌥ + S",
          descricao: "Ir_para_a_busca_da_listagem",
        });
      }
      if (this.standaloneCrudKeyboardFilterControl(container)) {
        shortcuts.push({
          teclas: "Ctrl / ⌘ + Shift + F",
          descricao: "Aplicar_os_filtros_da_listagem",
        });
      }
      if (table) {
        shortcuts.push({
          teclas: "Seta para cima + Seta para baixo",
          descricao: "Navegar_pelas_linhas_da_tabela",
        });
      }
      if (table && this.standaloneCrudKeyboardHasHandler(EDIT_HANDLER_NAMES)) {
        shortcuts.push(
          { teclas: "Enter", descricao: "Abrir_a_linha_em_foco" },
          { teclas: "Ctrl + E", descricao: "Editar_a_linha_em_foco" }
        );
      }
      if (table && this.standaloneCrudKeyboardSelectAllControl(table)) {
        shortcuts.push(
          {
            teclas: "Espaço",
            descricao: "Marcar_ou_desmarcar_a_linha_em_foco",
          },
          {
            teclas: "Ctrl / ⌘ + A",
            descricao: "Marcar_ou_desmarcar_as_linhas_exibidas",
          }
        );
      }
      shortcuts.push(
        { teclas: "Esc", descricao: "Fechar_a_camada_atual" },
        {
          teclas: "Ctrl / ⌘ + /",
          descricao: "Exibir_esta_lista_de_atalhos",
        }
      );
      return shortcuts;
    },
    openStandaloneCrudKeyboardShortcuts(container, table) {
      this.$root.$emit(STANDALONE_CRUD_SHORTCUTS_EVENT, {
        shortcuts: this.standaloneCrudKeyboardShortcuts(container, table),
      });
    },
    handleStandaloneCrudKeyboardFocus(event) {
      if (!this.isStandaloneCrudKeyboardPageActive()) return;
      this.syncStandaloneCrudKeyboardTables();
      const table = event.target?.closest?.(`.${ACTIVE_TABLE_CLASS}`);
      if (table) this.ensureStandaloneCrudKeyboardActiveRow(table);
    },
    handleStandaloneCrudKeyboardClick(event) {
      if (!this.isStandaloneCrudKeyboardPageActive()) return;
      const row = event.target?.closest?.("tbody tr");
      const table = row?.closest?.(`.${ACTIVE_TABLE_CLASS}`);
      if (!row || !table) return;
      const index = tableRows(table).indexOf(row);
      if (index >= 0) this.setStandaloneCrudKeyboardActiveRow(table, index);
    },
    handleStandaloneCrudKeyboardKeydown(event) {
      if (
        event.defaultPrevented ||
        !this.isStandaloneCrudKeyboardPageActive() ||
        document.querySelector(".swal2-container.swal2-backdrop-show")
      ) {
        return;
      }

      if (this.standaloneCrudKeyboardHasBaseFormDialog()) return;
      this.syncStandaloneCrudKeyboardTables();
      const container = this.standaloneCrudKeyboardContainer();
      const table = this.standaloneCrudKeyboardResolveTable(event.target);
      const key = String(event.key || "");
      const keyLower = key.toLowerCase();
      const ctrlOrCmd = event.ctrlKey || event.metaKey;

      if (key === "Tab") {
        const dialog = getTopActiveDialog(this.$el?.ownerDocument);
        if (dialog) trapTabWithin(dialog, event);
        return;
      }

      if (key === "Escape" || key === "Esc") {
        const dialog = getTopActiveDialog(this.$el?.ownerDocument);
        const closeControl =
          this.standaloneCrudKeyboardSafeCloseControl(dialog);
        if (!closeControl) return;
        consumeKeyboardEvent(event, true);
        activateControl(closeControl);
        return;
      }

      if (ctrlOrCmd && event.shiftKey && !event.altKey && keyLower === "f") {
        const filter = this.standaloneCrudKeyboardFilterControl(container);
        if (!filter) return;
        consumeKeyboardEvent(event, true);
        activateControl(filter);
        return;
      }

      if (isCrudSearchShortcut(event)) {
        if (!this.openStandaloneCrudKeyboardSearch(container)) return;
        consumeKeyboardEvent(event, true);
        return;
      }

      if (ctrlOrCmd && !event.shiftKey && !event.altKey && !event.repeat) {
        if (event.ctrlKey && !event.metaKey && keyLower === "e" && table) {
          if (
            isEditableKeyboardTarget(event.target) ||
            !event.target?.closest?.(`.${ACTIVE_TABLE_CLASS}`)
          ) {
            return;
          }
          this.ensureStandaloneCrudKeyboardActiveRow(table);
          if (
            !this.standaloneCrudKeyboardEditControl(
              this.standaloneCrudKeyboardActiveRow(table)
            )
          ) {
            return;
          }
          consumeKeyboardEvent(event, true);
          this.activateStandaloneCrudKeyboardRow(table);
          return;
        }

        if (keyLower === "i") {
          const insert = this.standaloneCrudKeyboardInsertControl(container);
          if (!insert) return;
          consumeKeyboardEvent(event, true);
          activateControl(insert);
          return;
        }

        if (
          keyLower === "a" &&
          table &&
          !isEditableKeyboardTarget(event.target) &&
          event.target?.closest?.(`.${ACTIVE_TABLE_CLASS}`)
        ) {
          const selectAll = this.standaloneCrudKeyboardSelectAllControl(table);
          if (!selectAll) return;
          consumeKeyboardEvent(event, true);
          activateControl(selectAll);
          return;
        }
      }

      if (
        key === "/" &&
        ctrlOrCmd &&
        !event.shiftKey &&
        !event.altKey &&
        !event.repeat
      ) {
        consumeKeyboardEvent(event, true);
        this.openStandaloneCrudKeyboardShortcuts(container, table);
        return;
      }

      if (
        event.repeat ||
        ctrlOrCmd ||
        event.altKey ||
        event.shiftKey ||
        isEditableKeyboardTarget(event.target) ||
        event.target?.closest?.("button, a, [role='button'], .v-input") ||
        !table ||
        !event.target?.closest?.(`.${ACTIVE_TABLE_CLASS}`)
      ) {
        return;
      }

      if (key === "ArrowDown" || key === "ArrowUp") {
        consumeKeyboardEvent(event, true);
        this.moveStandaloneCrudKeyboardActiveRow(
          table,
          key === "ArrowDown" ? 1 : -1
        );
        return;
      }

      if (key === " " || key === "Spacebar") {
        this.ensureStandaloneCrudKeyboardActiveRow(table);
        const selection = this.standaloneCrudKeyboardSelectionControl(
          this.standaloneCrudKeyboardActiveRow(table)
        );
        if (!selection) return;
        consumeKeyboardEvent(event, true);
        activateControl(selection);
        return;
      }

      if (key === "Enter") {
        this.ensureStandaloneCrudKeyboardActiveRow(table);
        if (
          !this.standaloneCrudKeyboardEditControl(
            this.standaloneCrudKeyboardActiveRow(table)
          )
        ) {
          return;
        }
        consumeKeyboardEvent(event, true);
        this.activateStandaloneCrudKeyboardRow(table);
      }
    },
  },
};
